import React from 'react';
import { Helmet } from 'react-helmet-async';
import { getOgImage } from '../lib/api';

const SITE = 'BriefingIQ';
const SITE_LEGAL = 'BriefingIQ, Inc.';
const BASE_DESC =
  'BriefingIQ & EventIQ — the AI-powered enterprise platform for executive briefing management and corporate event operations.';
const DEFAULT_LOCALE = 'en_US';
const DEFAULT_LANG = 'en';
const SITE_ORIGIN = (() => {
  try {
    return new URL(process.env.REACT_APP_BACKEND_URL).origin;
  } catch (e) {
    return 'https://briefingiq.com';
  }
})();

/**
 * SEO — per-page meta + structured data.
 *
 * Extra props over the base tags:
 *   - locale             (default en_US)
 *   - noindex            (bool)
 *   - breadcrumbs        [{ name, path }]  (adds BreadcrumbList JSON-LD)
 *   - jsonLd             single object or array of extra JSON-LD blocks to inject
 *   - article            { headline, description, image, datePublished, dateModified, author, tags }
 *                        (adds Article JSON-LD; when set, `type` is forced to "article")
 *   - product            { name, description, brand, category }
 *                        (adds SoftwareApplication JSON-LD)
 */
const SEO = ({
  title,
  description,
  path = '/',
  image,
  type = 'website',
  keywords = [],
  publishedTime,
  modifiedTime,
  author,
  locale = DEFAULT_LOCALE,
  noindex = false,
  breadcrumbs = [],
  jsonLd,
  article,
  product,
}) => {
  const fullTitle = title
    ? `${title} — ${SITE}`
    : `${SITE} & EventIQ — Enterprise Briefings & Corporate Events`;
  const desc = description || BASE_DESC;
  const origin = typeof window !== 'undefined' ? window.location.origin : SITE_ORIGIN;
  const url = `${origin}${path}`;
  const ogImg =
    image ||
    getOgImage(
      title || 'BriefingIQ & EventIQ',
      description || 'The Enterprise Platform for Briefings & Events'
    );
  const finalType = article ? 'article' : type;

  // --- JSON-LD blocks ---
  const blocks = [];

  // WebSite (only worth adding on the home page)
  if (path === '/' || path === '') {
    blocks.push({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITE,
      url: origin || 'https://briefingiq.com',
      publisher: {
        '@type': 'Organization',
        name: SITE_LEGAL,
        url: origin || 'https://briefingiq.com',
      },
    });
  }

  // BreadcrumbList
  if (breadcrumbs && breadcrumbs.length > 0) {
    blocks.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((b, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: b.name,
        item: `${origin}${b.path}`,
      })),
    });
  }

  // Article
  if (article) {
    blocks.push({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.headline || title,
      description: article.description || desc,
      image: article.image || ogImg,
      datePublished: article.datePublished || publishedTime,
      dateModified: article.dateModified || modifiedTime || publishedTime,
      author: {
        '@type': 'Person',
        name: article.author || author || 'BriefingIQ Team',
      },
      publisher: {
        '@type': 'Organization',
        name: SITE_LEGAL,
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': url,
      },
      keywords: (article.tags || keywords || []).join(', ') || undefined,
    });
  }

  // Product / SoftwareApplication
  if (product) {
    blocks.push({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: product.name,
      description: product.description || desc,
      applicationCategory: product.category || 'BusinessApplication',
      operatingSystem: 'Web',
      brand: {
        '@type': 'Brand',
        name: product.brand || SITE,
      },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
      },
      url,
    });
  }

  // User-provided extra blocks
  if (jsonLd) {
    if (Array.isArray(jsonLd)) blocks.push(...jsonLd);
    else blocks.push(jsonLd);
  }

  return (
    <Helmet prioritizeSeoTags>
      <html lang={DEFAULT_LANG} />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}
      <link rel="canonical" href={url} />

      {/* Locale + robots */}
      <meta property="og:locale" content={locale} />
      <meta
        name="robots"
        content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1'}
      />
      <meta name="theme-color" content="#0F172A" />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={finalType} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImg} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title || SITE} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImg} />
      <meta name="twitter:image:alt" content={title || SITE} />

      {/* Article-specific */}
      {(publishedTime || article?.datePublished) && (
        <meta property="article:published_time" content={publishedTime || article?.datePublished} />
      )}
      {(modifiedTime || article?.dateModified) && (
        <meta property="article:modified_time" content={modifiedTime || article?.dateModified} />
      )}
      {(author || article?.author) && (
        <meta property="article:author" content={author || article?.author} />
      )}
      {(article?.tags || []).map((t) => (
        <meta key={t} property="article:tag" content={t} />
      ))}

      {/* Structured data */}
      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
